<script lang="ts">
  import { untrack } from 'svelte';
  import type { ShellController } from './shell-controller.svelte';
  import type { SoundStore } from '../../audio/sound-store';
  import { socketState } from '../socket-state.svelte';
  import { AssetPaths } from '../../assets/asset-store';
  import { settingsOverlay } from '../settings-open.svelte';
  import { features } from '../../features/feature-state.svelte';
  import { CUSTOM_LOBBIES } from '../../features/overrides';

  interface Props {
    shell: ShellController;
    /** The last name typed into this box, from `localStorage['name']` — see `session-store.ts`. */
    rememberedName: string;
    sounds: SoundStore;
    musicSelection: number;
    onMusicSelect: (selection: number) => void;
  }

  const { shell, rememberedName, sounds, musicSelection, onMusicSelect }: Props = $props();

  // Seeded once from the prop, deliberately: this is the join form's initial value, not a binding
  // to whatever the caller passes later.
  // svelte-ignore state_referenced_locally
  let name = $state(rememberedName);
  let team = $state(2);
  let soundOn = $state(untrack(() => sounds.enabled));

  $effect(() =>
    sounds.subscribe(() => {
      soundOn = sounds.enabled;
    }),
  );

  // Mirrors the legacy client overwriting `NameArea` on a successful login: a fresh
  // `nameSuggestion` replaces whatever is typed, but the field stays editable afterward.
  $effect(() => {
    if (shell.nameSuggestion !== null) name = shell.nameSuggestion;
  });

  function onsubmit(event: SubmitEvent): void {
    event.preventDefault();
    shell.join({ name, team });
  }

  function toggleSound(): void {
    // A click is the gesture the AudioContext needs, and it may be the first one the page has seen.
    sounds.resume();
    sounds.enabled = !soundOn;
  }

  function changeMusic(event: Event): void {
    const select = event.currentTarget;
    if (!(select instanceof HTMLSelectElement)) return;
    // A change on a select is a gesture, and it may be the first one the page has seen.
    sounds.resume();
    onMusicSelect(Number(select.value));
  }
</script>

<form id="join-form" class="wis-panel wis-panel--main" {onsubmit} autocomplete="off">
  <label class="wis-row pregame__nickname" for="name">Nickname:</label>
  <input id="name" type="text" placeholder="Nickname" maxlength="35" bind:value={name} />

  <div class="wis-teams pregame__teams">
    <span class="pregame__teams-label">Team:</span>
    <div class="pregame__teams-row">
      <input id="radioAuto" type="radio" name="team" value={2} bind:group={team} />
      <label for="radioAuto">Auto</label>
      <input id="radioBlue" type="radio" name="team" value={0} bind:group={team} />
      <label class="pregame__blue" for="radioBlue">Blue</label>
      <input id="radioRed" type="radio" name="team" value={1} bind:group={team} />
      <label class="pregame__red" for="radioRed">Red</label>
    </div>
  </div>

  {#if shell.playDisabled}
    <span id="status" class="wis-row pregame__status" role="status">{shell.status}</span>
  {:else}
    <button id="play" class="wis-btn-play pregame__play" type="submit">Play</button>
  {/if}

  <div class="wis-row pregame__world">
    <span class="pregame__world-label">World: {socketState.serverNum + 1}</span>
    <button
      class="wis-btn pregame__change"
      type="button"
      onclick={() => {
        shell.navigate('worlds');
      }}>Change</button
    >
  </div>

  {#if features.enabled(CUSTOM_LOBBIES)}
    <div class="wis-row pregame__lobbies">
      <button
        class="wis-btn pregame__lobbies-btn"
        type="button"
        data-testid="pregame-lobbies"
        onclick={() => {
          shell.navigate('lobbies');
        }}>5v5 Lobbies</button
      >
    </div>
  {/if}

  <div class="wis-row pregame__music">
    <label class="pregame__music-label" for="music">Music:</label>
    <select
      id="music"
      class="wis-select"
      data-testid="shell-music"
      value={String(musicSelection)}
      onchange={changeMusic}
    >
      <option value="0">None</option>
      <option value="1">1</option>
    </select>
  </div>

  <!-- Menu_1's art has a detached lower bar; this row is what it was drawn to hold. -->
  <div class="wis-row pregame__social">
    <a
      class="wis-btn-social"
      href="https://discord.gg/YqK2c2H"
      target="_blank"
      rel="noreferrer noopener"
      aria-label="War In Space on Discord"
    >
      <i class="wis-icon wis-icon--discord"></i>
    </a>
    <button
      class="wis-btn-social"
      type="button"
      aria-label="Settings"
      data-testid="shell-settings"
      onclick={() => {
        settingsOverlay.show();
      }}
    >
      <i class="wis-icon wis-icon--gear"></i>
    </button>
    <button
      class="wis-btn-social"
      type="button"
      aria-pressed={soundOn}
      aria-label={soundOn ? 'Mute sound' : 'Unmute sound'}
      data-testid="shell-sound-toggle"
      onclick={toggleSound}
    >
      <img src={AssetPaths.audioIcon(soundOn)} width="34" height="34" alt="" />
    </button>
  </div>
</form>

<style>
  /* Menu_1's logo occupies the plate down to about y=120. */
  .pregame__nickname {
    top: 128px;
    font-size: 16px;
  }

  /* Menu_1's plate ends at x=289 of the 326px box — the rest is its cable ornament, so a row
     centred on the box sits 11px right of the plate and the widest of them crosses the rail. */
  .pregame__nickname,
  .pregame__teams,
  .pregame__status,
  .pregame__world,
  .pregame__music,
  .pregame__lobbies {
    right: 23px;
  }

  #name {
    position: absolute;
    top: 151px;
    left: 36px;
  }

  .pregame__teams {
    position: absolute;
    top: 188px;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 52px;
    white-space: nowrap;
  }

  .pregame__teams-row {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 4px;
  }

  .pregame__teams-label {
    font-size: 15px;
  }

  .pregame__blue {
    color: #1b1bb4;
  }

  .pregame__red {
    color: #a81414;
  }

  .pregame__play {
    position: absolute;
    top: 247px;
    left: 79px;
    width: 167px;
    height: 61px;
    padding: 0;
  }

  /* Where the Play button goes once the socket is up. Our messages are longer than one word, so
     this is smaller than the plate's 16px and allowed two lines. */
  .pregame__status {
    top: 232px;
    height: 34px;
    padding: 0 24px;
    font-size: 13px;
    line-height: 1.25;
  }

  .pregame__world {
    top: 315px;
    height: 34px;
    white-space: nowrap;
  }

  .pregame__world-label {
    font-size: 12px;
    vertical-align: middle;
  }

  .pregame__change {
    color: #f2f2f2;
    font-size: 12px;
    padding: 10px 22px;
    vertical-align: middle;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .pregame__music {
    top: 356px;
    height: 30px;
    white-space: nowrap;
  }

  .pregame__music-label {
    font-size: 12px;
    vertical-align: middle;
    margin-right: 4px;
  }

  .pregame__lobbies {
    top: 396px;
    height: 34px;
    white-space: nowrap;
  }

  .pregame__lobbies-btn {
    color: #f2f2f2;
    font-size: 12px;
    padding: 10px 26px;
    text-shadow: 0 2px 3px rgb(0 0 0 / 80%);
  }

  .pregame__social {
    top: 448px;
    left: 0;
    width: 297px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .pregame__social :global(.wis-btn-social) {
    display: grid;
    place-items: center;
    width: 54px;
    height: 46px;
    margin: 0;
    padding: 0;
    background-color: #55554f;
    background-image:
      linear-gradient(rgb(255 255 255 / 22%), rgb(255 255 255 / 4%) 40%, rgb(0 0 0 / 14%)),
      linear-gradient(#6d6d68, #454541 80%, #3c3c39);
    box-shadow:
      inset 0 2px 0 rgb(255 255 255 / 45%),
      inset 0 -2px 0 rgb(0 0 0 / 55%),
      0 0 0 1px #1b1b19,
      0 4px 9px rgb(0 0 0 / 55%);
  }

  .pregame__social :global(.wis-btn-social:hover) {
    background-image:
      linear-gradient(rgb(255 255 255 / 34%), rgb(255 255 255 / 10%) 40%, rgb(0 0 0 / 10%)),
      linear-gradient(#7e7e78, #4f4f4b);
  }

  .pregame__social :global(.wis-btn-social:active) {
    box-shadow:
      inset 0 2px 5px rgb(0 0 0 / 75%),
      inset 0 -1px 0 rgb(255 255 255 / 14%);
  }

  .pregame__social :global(.wis-icon),
  .pregame__social img {
    filter: brightness(0) invert(1) drop-shadow(0 1px 1px rgb(0 0 0 / 60%));
  }

  .pregame__social :global(.wis-icon) {
    width: 36px;
    height: 36px;
    margin: 0;
  }

  .pregame__social img {
    width: 34px;
    height: 34px;
    margin: 0;
  }
</style>
